import type { Editor } from "@tiptap/react";
import { BubbleMenu } from "@tiptap/react/menus";
import { AlignLeft, AlignCenter, AlignRight } from "lucide-react";
import type { ImageWidth, ImageAlign } from "../extensions/custom-image";
import { cn } from "@/lib/utils";

const IMAGE_WIDTHS: ImageWidth[] = ["25%", "50%", "75%", "100%"];

const IMAGE_ALIGNS: { value: ImageAlign; icon: typeof AlignLeft }[] = [
  { value: "left", icon: AlignLeft },
  { value: "center", icon: AlignCenter },
  { value: "right", icon: AlignRight },
];

const IMAGE_ALIGN_LABELS: Record<ImageAlign, string> = {
  left: "왼쪽 정렬",
  center: "가운데 정렬",
  right: "오른쪽 정렬",
};

interface ImageBubbleMenuProps {
  editor: Editor;
}

export function ImageBubbleMenu({ editor }: ImageBubbleMenuProps) {
  const attrs = editor.getAttributes("image");
  const currentWidth = (attrs["data-width"] as ImageWidth) || "100%";
  const currentAlign = (attrs["data-align"] as ImageAlign) || null;

  const handleWidthChange = (width: ImageWidth) => {
    editor
      .chain()
      .focus()
      .updateAttributes("image", { "data-width": width })
      .run();
  };

  const handleAlignChange = (align: ImageAlign) => {
    const nextAlign = currentAlign === align ? null : align;

    editor
      .chain()
      .focus()
      .updateAttributes("image", { "data-align": nextAlign })
      .run();
  };

  return (
    <BubbleMenu
      editor={editor}
      tippyOptions={{ duration: 150 }}
      shouldShow={({ editor }) => editor.isActive("image")}
      className="flex items-center gap-0.5 rounded-md border bg-background p-1 shadow-md"
    >
      {IMAGE_WIDTHS.map((width) => (
        <button
          key={width}
          type="button"
          onClick={() => handleWidthChange(width)}
          className={cn(
            "rounded px-2 py-1 text-xs font-medium transition-colors",
            "hover:bg-accent hover:text-accent-foreground",
            currentWidth === width
              ? "bg-accent text-accent-foreground"
              : "text-muted-foreground"
          )}
        >
          {width}
        </button>
      ))}

      <div className="mx-0.5 h-4 w-px bg-border" />

      {IMAGE_ALIGNS.map(({ value, icon: Icon }) => (
        <button
          key={value}
          type="button"
          onClick={() => handleAlignChange(value)}
          aria-label={IMAGE_ALIGN_LABELS[value]}
          aria-pressed={currentAlign === value}
          className={cn(
            "rounded p-1 transition-colors",
            "hover:bg-accent hover:text-accent-foreground",
            currentAlign === value
              ? "bg-accent text-accent-foreground"
              : "text-muted-foreground"
          )}
        >
          <Icon className="h-3.5 w-3.5" />
        </button>
      ))}
    </BubbleMenu>
  );
}
